import {
  useCallback, useEffect, useState,
} from 'react';

export const VERIFICATION_COOLDOWN_SECONDS = 60;

const COOLDOWN_MS = VERIFICATION_COOLDOWN_SECONDS * 1000;

const remainingSeconds = (issuedAtMs: number, now: number): number => {
  if (!issuedAtMs) {
    return 0;
  }
  return Math.max(0, Math.ceil((issuedAtMs + COOLDOWN_MS - now) / 1000));
};

/**
 * Single-channel OTP issue cooldown. Call once per channel in LoginContainer
 * so email and SMS keep independent timers across step remounts.
 *
 * Gate sends with `cooldownSeconds === 0`, then call `startCooldown()` before issuing.
 */
const useVerificationCooldown = () => {
  const [issuedAtMs, setIssuedAtMs] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const cooldownSeconds = remainingSeconds(issuedAtMs, now);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return undefined;
    }
    const timerId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, [cooldownSeconds, issuedAtMs]);

  const startCooldown = useCallback(() => {
    const next = Date.now();
    setIssuedAtMs(next);
    setNow(next);
  }, []);

  const reset = useCallback(() => {
    setIssuedAtMs(0);
  }, []);

  return {
    cooldownSeconds,
    startCooldown,
    reset,
  };
};

export default useVerificationCooldown;
